/* eslint-disable class-methods-use-this */
import { Injectable } from '@angular/core';
import { SessionDocumentInfo } from '../models/session/session-document-info.model';
import {
  AppliedPayableRestriction,
  PayablePaymentStatus,
  PaymentPreviewV1Response,
} from '../models';

@Injectable({
  providedIn: 'root',
})
// TODO: Eventually use NGRX selectors to get this data out of store instead of this.
export class PaymentDocumentHelperService {
  public getSubTotal(documents: SessionDocumentInfo[]): number {
    return documents
      .filter((d) => d.isPayable && !d.isCreditMemo)
      .map((x) => x.payableAmount)
      .reduce((acc, curr) => acc + curr, 0);
  }

  public getCreditMemoSubTotal(documents: SessionDocumentInfo[]): number {
    return documents
      .filter((d) => d.isCreditMemo)
      .map((x) => {
        return x.payableAmount;
      })
      .reduce((acc, curr) => acc + curr, 0);
  }

  applyPayableRestrictions(
    paymentPreview: PaymentPreviewV1Response,
    documents: SessionDocumentInfo[],
    allowStatements: boolean,
    allowOverPay: boolean,
    allowBankPayments: boolean,
    allowCardPayments: boolean,
    allowOverrideCardPayments: boolean,
    blockDuplicatePayments: boolean,
  ): AppliedPayableRestriction[] {
    const payload: AppliedPayableRestriction[] = [];
    const payables = paymentPreview.payments.flatMap((p) => p.payment.payables);

    documents.forEach((document) => {
      const sessionDocumentIncludedInPreviewPayables = payables.find(
        (p) => p.burn === document.urn,
      );

      // Apply statement restriction before the early return below so that the
      // tooltip hint is preserved even when the document was excluded from the
      // preview request (statements are filtered out of the request when
      // allowStatements is false, so they won't appear in the payables response).
      if (document.documentType === 'statement' && !allowStatements) {
        payload.push({
          documentUrn: document.urn,
          isRejected: true,
          isPayableWithBank: false,
          isPayableWithCard: false,
          hints: ['ARC_PAYMENT_DOCUMENT_TYPE_NOT_PAYABLE'],
        });
        return;
      }

      if (sessionDocumentIncludedInPreviewPayables == null) {
        // If the document isn't included in the payment preview payables response,
        // it was excluded in the preview request itself due to a duplicate payment
        // hard block or soft block deselection. In this case we don't want the
        // restrictions already set in the store for such documents to be overridden
        // with defaults.
        return;
      }

      const hasAvailablePaymentMethods =
        allowBankPayments || allowCardPayments || allowOverrideCardPayments;

      let result: AppliedPayableRestriction = {
        documentUrn: document.urn,
        isRejected: false,
        isPayableWithBank: allowBankPayments,
        isPayableWithCard: allowCardPayments || allowOverrideCardPayments,
        hints: [],
      };

      // First, apply the flexible template rules
      if (
        sessionDocumentIncludedInPreviewPayables?.flexibleTemplates
          ?.isApplicable
      ) {
        const { availableRules, rejectedReasons } =
          sessionDocumentIncludedInPreviewPayables.flexibleTemplates;
        const isTemplatePayableWithBank = availableRules.includes(
          'ALLOW_BANK_PAYMENTS',
        );
        const isTemplatePayableWithCard = availableRules.includes(
          'ALLOW_CARD_PAYMENTS',
        );

        if (availableRules.length === 0) {
          // If no payment methods are enabled for the template, show as unpayable with a hint
          result = {
            ...result,
            isRejected: true,
            isPayableWithBank: false,
            isPayableWithCard: false,
            hints: ['ARC_PAYMENT_DOCUMENT_TYPE_NOT_PAYABLE'],
          };
        } else {
          // If at least one payment method is enabled, show a hint
          if (result.isPayableWithBank && !isTemplatePayableWithBank) {
            result.isPayableWithBank = false;
            result.hints = ['ARC_PAYMENT_DOCUMENT_TYPE_NOT_PAYABLE_WITH_ACH'];
          }

          if (result.isPayableWithCard && !isTemplatePayableWithCard) {
            result.isPayableWithCard = false;
            result.hints = ['ARC_PAYMENT_DOCUMENT_TYPE_NOT_PAYABLE_WITH_CARD'];
          }

          result.isRejected ||= rejectedReasons.length > 0;
        }
      }

      // Next, apply the timed card acceptance rules
      if (
        !document.isCreditMemo &&
        sessionDocumentIncludedInPreviewPayables?.timedCardAcceptance
          ?.isGracePeriodExpired
      ) {
        if (result.isPayableWithCard) {
          result.isPayableWithCard = false;
          result.hints.push('ARC_PAYMENT_DOCUMENT_NOT_PAYABLE_WITH_CARD');
        }

        result.isRejected ||=
          sessionDocumentIncludedInPreviewPayables.timedCardAcceptance.isRejected;
      }

      if (
        sessionDocumentIncludedInPreviewPayables?.hasPastPayments != null &&
        !document.isCreditMemo
      ) {
        result = this.applyDuplicatePaymentRules(
          result,
          sessionDocumentIncludedInPreviewPayables?.hasPastPayments,
          blockDuplicatePayments,
          allowOverPay,
        );
      }

      result.isRejected ||=
        hasAvailablePaymentMethods &&
        !result.isPayableWithBank &&
        !result.isPayableWithCard;

      payload.push(result);
    });

    return payload;
  }

  private applyDuplicatePaymentRules(
    base: AppliedPayableRestriction,
    status: PayablePaymentStatus,
    blockDuplicatePayments: boolean,
    allowOverPay: boolean,
  ): AppliedPayableRestriction {
    const result: AppliedPayableRestriction = {
      ...base,
      hints: [...(base.hints ?? [])],
      // Carry domain status onto the doc (via reducer → isPaid) so banners/selectors
      // do not depend on tooltip i18n keys surviving.
      pastPaymentStatus: status,
    };

    if (status === PayablePaymentStatus.HasPastPayment) {
      result.hints.push('ARC_PAYMENT_DUPLICATE_PAYMENT_TOOLTIP_WARNING_RECENT');
      return result;
    }

    const isPaidInFull =
      status === PayablePaymentStatus.FullyPaid ||
      status === PayablePaymentStatus.FullyConsumed;

    if (!isPaidInFull) {
      return result;
    }

    if (blockDuplicatePayments && !allowOverPay) {
      result.hints.push('ARC_PAYMENT_DUPLICATE_PAYMENT_TOOLTIP_WARNING_HARD');
      result.isRejected = true;
    } else {
      result.hints.push('ARC_PAYMENT_DUPLICATE_PAYMENT_TOOLTIP_WARNING_SOFT');
    }

    return result;
  }
}
